import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useDispatch } from "react-redux";
import { setIsAuthenticationOpen } from "lib/redux/uiSlice";
import AutoFillIcons from "public/assets/AutofillIcon.svg"

interface MobileMenuProps {
    isOpen: boolean;
    onClose: () => void;
    accessToken: string | null;
    handleLogout: () => void;
    handleSaveResume: () => void;
    handleResumeShare: () => void;
    toggleImportComponentState: () => void;
}

const MobileMenu: React.FC<MobileMenuProps> = ({
    isOpen,
    onClose,
    accessToken,
    handleLogout,
    handleSaveResume,
    handleResumeShare,
    toggleImportComponentState
}) => {
    const dispatch = useDispatch();

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 lg:hidden">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/50" onClick={onClose} />

            {/* Menu panel */}
            <div className="absolute right-0 top-0 h-full w-64 bg-white p-5 shadow-xl transform transition-transform duration-300 ease-in-out">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="font-semibold text-lg">Menu</h2>
                    <button onClick={onClose} className="p-2">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>
                </div>

                <div className="flex flex-col space-y-4">
                    {/* Autofill button */}
                    <button
                        onClick={() => {
                            toggleImportComponentState();
                            onClose();
                        }}
                        className="flex items-center gap-2 py-3 px-4 bg-blue-50 rounded-lg text-blue-600 font-medium"
                    >
                        <svg width="18" height="20" viewBox="0 0 18 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M6.08268 13.3333C6.50435 13.7424 8.16602 14.8333 8.16602 15.4166M8.16602 15.4166C8.16602 15.9999 6.50435 17.0908 6.08268 17.4999M8.16602 15.4166H1.49935" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M9.83333 18.3334H10.0608C12.7775 18.3334 14.1375 18.3334 15.0808 17.6684C15.3493 17.4802 15.5893 17.2542 15.7933 16.9976C16.5 16.1092 16.5 14.8309 16.5 12.2726V10.1517C16.5 7.68258 16.5 6.44758 16.1092 5.46175C15.4808 3.87591 14.1525 2.62591 12.4675 2.03425C11.4208 1.66675 10.11 1.66675 7.485 1.66675C5.98667 1.66675 5.23667 1.66675 4.63833 1.87675C3.67583 2.21508 2.91667 2.92925 2.5575 3.83508C2.33333 4.39841 2.33333 5.10425 2.33333 6.51508V10.8334" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M16.5 10.0001C16.5 9.26344 16.2074 8.55697 15.6865 8.03609C15.1656 7.51521 14.4591 7.22258 13.7225 7.22258C13.1675 7.22258 12.5133 7.31925 11.9742 7.17508C11.7386 7.11168 11.5238 6.98746 11.3514 6.81487C11.1789 6.64228 11.0549 6.42739 10.9917 6.19175C10.8475 5.65258 10.9442 4.99841 10.9442 4.44341C10.9439 3.70692 10.6512 3.00067 10.1304 2.47996C9.6095 1.95926 8.90316 1.66675 8.16667 1.66675" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>

                        <span>Autofill resume</span>
                    </button>

                    {/* Save button */}
                    <button
                        onClick={() => {
                            handleSaveResume();
                            onClose();
                        }}
                        className="flex items-center gap-2 py-3 px-4 bg-blue-50 rounded-lg text-blue-600 font-medium"
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                            <polyline points="17 21 17 13 7 13 7 21"></polyline>
                            <polyline points="7 3 7 8 15 8"></polyline>
                        </svg>
                        <span>Save resume</span>
                    </button>

                    {/* Share button */}
                    <button
                        onClick={() => {
                            handleResumeShare();
                            onClose();
                        }}
                        className="flex items-center gap-2 py-3 px-4 bg-blue-50 rounded-lg text-blue-600 font-medium"
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="18" cy="5" r="3"></circle>
                            <circle cx="6" cy="12" r="3"></circle>
                            <circle cx="18" cy="19" r="3"></circle>
                            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
                        </svg>
                        <span>Share resume</span>
                    </button>

                    <hr className="border-gray-200" />

                    {/* Navigation links */}
                    <Link href="/" className="flex gap-x-2 items-center py-3 hover:text-blue-500 transition-colors">
                        <Image
                            className="bg-gradient-to-r from-[#88c4fc] to-[#1b91fe] p-1 rounded-full"
                            src="/icons/home.svg"
                            alt="home icon"
                            width={30}
                            height={30}
                        />
                        <span>Home</span>
                    </Link>

                    {accessToken && (
                        <Link href="/dashboard" className="flex gap-x-2 items-center py-3 hover:text-blue-500 transition-colors">
                            <Image
                                className="bg-gradient-to-r from-[#88c4fc] to-[#1b91fe] p-1 rounded-full"
                                src="/assets/dashboardIcon.svg"
                                alt="dashboard icon"
                                width={30}
                                height={30}
                            />
                            <span>Dashboard</span>
                        </Link>
                    )}

                    {accessToken ? (
                        <Link href="/score-check?source=builder" className="flex gap-x-2 items-center py-3 hover:text-blue-500 transition-colors">
                        <Image
                            className="bg-gradient-to-r from-[#88c4fc] to-[#1b91fe] p-1 rounded-full"
                            src="/icons/checkScore.svg"
                            alt="score check icon"
                            width={30}
                            height={30}
                        />
                        <span>Check Score</span>
                    </Link>
                    ):
                    <button onClick={() => dispatch(setIsAuthenticationOpen(true))} className="flex gap-x-2 items-center py-3 hover:text-blue-500 transition-colors">
                        <Image
                            className="bg-gradient-to-r from-[#88c4fc] to-[#1b91fe] p-1 rounded-full"
                            src="/icons/checkScore.svg"
                            alt="score check icon"
                            width={30}
                            height={30}
                        />
                        <span>Check Score</span>
                    </button>
                    }

                    {accessToken ? (
                        <button onClick={handleLogout} className="flex gap-x-2 items-center py-3 hover:text-blue-500 transition-colors">
                            <Image
                                className="bg-gradient-to-r from-[#88c4fc] to-[#1b91fe] p-1 rounded-full"
                                src="/icons/logout.svg"
                                alt="logout icon"
                                width={30}
                                height={30}
                            />
                            <span>Sign out</span>
                        </button>
                    ) : (
                        <button
                            onClick={() => dispatch(setIsAuthenticationOpen(true))}
                            className="flex gap-x-2 items-center py-3 hover:text-blue-500 transition-colors"
                        >
                            <Image
                                className="bg-gradient-to-r from-[#88c4fc] to-[#1b91fe] p-1 rounded-full"
                                src="/icons/logout.svg"
                                alt="login icon"
                                width={30}
                                height={30}
                            />
                            <span>Sign in</span>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MobileMenu;